"use client"

import type React from "react"
import { GiPapers } from "react-icons/gi"
import { useTheme } from "../../context"
import { Link } from "react-router-dom"
import { BriefcaseIcon } from "lucide-react"

interface SidebarProps {
  sidebarDisplay: boolean
  isAdsVisible?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarDisplay, isAdsVisible }) => {
  const { theme } = useTheme()

  const navigationItems = [
    {
      path: "/home",
      label: "Home",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
          <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
        </svg>
      ),
    },
    {
      path: "/dashboard",
      label: "Dashboard",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path
            fillRule="evenodd"
            d="M3 6a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3V6ZM3 15.75a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-2.25Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3v-2.25Z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      path: "/interview-setup",
      label: "New Interview",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-circle-question-mark-icon lucide-circle-question-mark"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
      ),
    },
    {
      path: "/interviews",
      label: "My Interviews",
      icon: <BriefcaseIcon className="w-5 h-5" />,
    },
    {
      path: "/profile",
      label: "Profile",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path
            fillRule="evenodd"
            d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
            clipRule="evenodd"
          />
        </svg>
      )
    },
    {
      path: "/contact-us",
      label: "Contact Us",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
          <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
        </svg>
      ),
    },

  ]

  const isActive = (path: string) => window.location.pathname === path

  return (
    <aside
      className={`min-h-screen  pt-20 ${sidebarDisplay ? "flex w-auto" : isAdsVisible ? "hidden" : "hidden xl:flex xl:w-[280px]"
        } flex-col transition-all duration-300 ease-in-out z-[1000] ${theme === "dark"
          ? "bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/70 to-[#1a1a1a]/90 border-r border-slate-700/50"
          : "bg-gradient-to-b from-white via-slate-50 to-white border-r border-slate-200/60"
        } backdrop-blur-xl`}
    >

      <nav className="flex-1 p-4 space-y-2">
        <div className="mb-6">
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <Link key={item.path} to={item.path} className="block">
                <div
                  className={`group relative flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 ${isActive(item.path)
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600  text-white shadow-lg shadow-blue-500/25"
                    : theme === "dark"
                      ? "text-slate-300 hover:bg-slate-800/50 hover:text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                >
                  {isActive(item.path) && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
                  )}

                  <div className={`flex-shrink-0 ${isActive(item.path) ? "text-white" : ""}`}>{item.icon}</div>

                  <span className="font-medium text-sm truncate">{item.label}</span>

                  {isActive(item.path) && (
                    <svg
                      className="w-4 h-4 ml-auto text-white/70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

    </aside>
  )
}

export default Sidebar
